/**
 * PrescriptionFullList Component
 *
 * Renders the Alphabetical list tab on /prescription-tools.
 * Fetches the merged works + studies for the selected Prescription List,
 * then displays a toolbar (search + type filter + download), an A-Z table,
 * and a "no results" empty state.
 */

import Icon from '@/components/Icon'
import { Loading } from '@/components/Loading'
import RichText from '@/components/RichText'
import { useKontentHomeConfig } from '@/components/contexts/KontentHomeConfigProvider'
import {
	getGAPageContext,
	PRESCRIPTION_GA_EVENT,
} from '@/components/prescriptions/utils/prescriptionGAEvents'
import { sendPrescriptionGAEvent } from '@/components/prescriptions/utils/sendPrescriptionGAEvent'
import { fetchPrescriptionList } from '@/fetchers/fetchPrescription'
import { getPrescriptionListDocs } from '@/lib/getPrescriptionDocsFile'
import type { Mapping } from '@/types'
import type {
	PrescriptionTextsStudy,
	PrescriptionTextsWork,
} from '@/types/fetchPrescriptionTexts'
import { isRichtextElementEmpty } from '@/utils'
import type { Elements, IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import JsFileDownloader from 'js-file-downloader'
import { queryTypes, useQueryStates } from 'next-usequerystate'
import { Button, FormGroupSelect, FormGroupText } from 'nsw-ds-react'
import type { FormOption } from 'nsw-ds-react/dist/component/forms'
import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type ReactNode,
} from 'react'
import sanitizeHtml from 'sanitize-html'

export interface PrescriptionFullListProps {
	tabContent?: Elements.RichTextElement
	mappings?: Mapping[]
	linkedItems?: IContentItemsContainer
	listcodename: string
}

const TYPE_FILTER_OPTIONS: FormOption[] = [
	{ text: 'Prose fiction', value: 'Prose fiction' },
	{ text: 'Poetry', value: 'Poetry' },
	{ text: 'Drama', value: 'Drama' },
	{ text: 'Film', value: 'Film' },
	{ text: 'Nonfiction', value: 'Nonfiction' },
	{ text: 'Media', value: 'Media' },
	{ text: 'Performance poetry', value: 'Performance poetry' },
	{ text: 'Speech', value: 'Speech' },
	{ text: 'Author', value: 'Author' },
]

const TABLE_COLUMNS = [
	'Author',
	'Title',
	'Publication details',
	'Course details',
	'Type',
]

type WorkRow = {
	kind: 'work'
	id: string
	author: string
	work: PrescriptionTextsWork
}

type StudyRow = {
	kind: 'study'
	id: string
	author: string
	study: PrescriptionTextsStudy
}

type Row = WorkRow | StudyRow

const buildCourseCell = (
	prescriptions: PrescriptionTextsWork['prescriptions'],
): string =>
	prescriptions
		.map((p) => {
			const option = p['prescription-option']?.trim()
			return option
				? `${p.course}: ${p['focus-area']} (${option})`
				: `${p.course}: ${p['focus-area']}`
		})
		.join('\n')

const isExternalUrl = (url: string): boolean => {
	if (!/^https?:\/\//i.test(url)) {
		return false
	}
	if (typeof window === 'undefined') {
		return false
	}
	try {
		const parsed = new URL(url)
		return parsed.host !== window.location.host
	} catch {
		return false
	}
}

const TITLE_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
	allowedTags: ['em', 'i', 'strong', 'b', 'br'],
	allowedAttributes: {},
}

const sanitizeTitleHtml = (htmlTitle: string): string =>
	sanitizeHtml(htmlTitle ?? '', TITLE_SANITIZE_OPTIONS).trim()

const renderTitleCell = (work: PrescriptionTextsWork): ReactNode => {
	const titleHtml = sanitizeTitleHtml(work.title)
	if (!titleHtml) return null
	const titleNode = (
		// eslint-disable-next-line react/no-danger
		<span dangerouslySetInnerHTML={{ __html: titleHtml }} />
	)
	const titleLink = work['title-link']
	if (!titleLink) {
		return titleNode
	}
	if (isExternalUrl(titleLink)) {
		return (
			<a href={titleLink} target="_blank" rel="noopener noreferrer">
				{titleNode}
			</a>
		)
	}
	return <a href={titleLink}>{titleNode}</a>
}

export function PrescriptionFullList({
	tabContent,
	mappings = [],
	linkedItems = {},
	listcodename,
}: PrescriptionFullListProps) {
	const { preview } = useKontentHomeConfig()
	const showTabContent = tabContent && !isRichtextElementEmpty(tabContent)

	const [qs, setQS] = useQueryStates({
		q: queryTypes.string.withDefault(''),
		type: queryTypes.string.withDefault(''),
	})

	const [searchQuery, setSearchQuery] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')
	const [typeFilter, setTypeFilter] = useState('')
	const [isDownloading, setIsDownloading] = useState(false)
	const refDownloadAbortController = useRef<AbortController | null>(null)
	const refUserInteracted = useRef(false)

	useEffect(() => {
		if (qs.q !== debouncedSearch) {
			//setSearchQuery(qs.q)
			//setDebouncedSearch(qs.q)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [qs.q])

	useEffect(() => {
		if (qs.type !== typeFilter) {
			//setTypeFilter(qs.type)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [qs.type])

	useEffect(() => {
		const id = setTimeout(() => setDebouncedSearch(searchQuery), 300)
		return () => clearTimeout(id)
	}, [searchQuery])

	useEffect(() => {
		if (!refUserInteracted.current) return
		const trimmed = debouncedSearch.trim()
		if ((qs.q || '') === trimmed) return
		//setQS({ q: trimmed || null }, DEFAULT_NUQS_TRANSITION_OPTIONS)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch])

	useEffect(() => {
		return () => {
			refDownloadAbortController.current?.abort()
		}
	}, [])

	const trimmedSearch = debouncedSearch.trim()

	const { data, isFetching } = useQuery({
		queryKey: [
			'prescription-list',
			listcodename,
			trimmedSearch,
			typeFilter,
			preview,
		],
		queryFn: () =>
			fetchPrescriptionList({
				listcodename,
				search: trimmedSearch,
				prescriptionType: typeFilter,
				preview,
			}),
		enabled: Boolean(listcodename),
	})

	const list = data?.json?.data
	const works = useMemo(() => list?.works ?? [], [list])
	const studies = useMemo(() => list?.studies ?? [], [list])

	const mergedRows = useMemo<Row[]>(() => {
		const workRows: Row[] = works.map((work) => ({
			kind: 'work',
			id: work.id,
			author: work.author,
			work,
		}))
		const studyRows: Row[] = studies.map((study) => ({
			kind: 'study',
			id: study.id,
			author: study.author,
			study,
		}))
		return [...workRows, ...studyRows]
	}, [works, studies])

	const filteredRows = useMemo<Row[]>(() => {
		return mergedRows
			.map((row, index) => ({ row, index }))
			.sort((a, b) => {
				const cmp = a.row.author.localeCompare(
					b.row.author,
					undefined,
					{ sensitivity: 'base' },
				)
				return cmp !== 0 ? cmp : a.index - b.index
			})
			.map(({ row }) => row)
	}, [mergedRows])

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		refUserInteracted.current = true
		setSearchQuery(e.target.value)
	}

	const handleSearchClear = () => {
		refUserInteracted.current = true
		setSearchQuery('')
		setDebouncedSearch('')
	}

	const handleTypeFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value
		setTypeFilter(value)
		//setQS({ type: value || null }, DEFAULT_NUQS_TRANSITION_OPTIONS)
	}

	const handleReset = () => {
		refUserInteracted.current = true
		setSearchQuery('')
		setDebouncedSearch('')
		setTypeFilter('')
		//setQS({ q: null, type: null }, DEFAULT_NUQS_TRANSITION_OPTIONS)
	}

	const handleDownload = async () => {
		if (isDownloading || !listcodename) return

		const meta = list?.['prescriptions-list']
		sendPrescriptionGAEvent({
			event: PRESCRIPTION_GA_EVENT.tool2ListDownload,
			...getGAPageContext(),
			prescriptions_list: meta ? `${meta.title} (${meta.years})` : '',
		})

		refDownloadAbortController.current?.abort()
		const controller = new AbortController()
		refDownloadAbortController.current = controller
		setIsDownloading(true)
		try {
			const { ok, output, message } = await getPrescriptionListDocs(
				{ listcodename, pdf: false, isPreviewMode: preview },
				controller.signal,
			)
			if (controller.signal.aborted) return
			if (ok && output && output.code === 200 && output.path) {
				new JsFileDownloader({
					url: output.path,
					filename: output.name ?? undefined,
					autoStart: true,
					forceDesktopMode: true,
				})
			} else if (!controller.signal.aborted) {
				console.warn(
					'Prescription list download failed:',
					output?.message || message,
				)
			}
		} catch (err) {
			if (!controller.signal.aborted) {
				console.warn('Prescription list download error:', err)
			}
		} finally {
			if (refDownloadAbortController.current === controller) {
				setIsDownloading(false)
			}
		}
	}

	const showEmptyState = !isFetching && filteredRows.length === 0

	return (
		<div className="p-4">
			{showTabContent && (
				<div className="mb-6 text-nsw-grey-01">
					<RichText
						richTextElement={tabContent}
						linkedItems={linkedItems}
						mappings={mappings}
					/>
				</div>
			)}

			<div className="flex flex-col gap-4 mb-6 md:flex-row md:items-stretch">
				<div className="flex-1 min-w-0">
					<FormGroupText
						className={clsx(
							'relative',
							searchQuery.length > 0 &&
								'searchbar-with-clear-button',
						)}
						htmlId="prescription-list-search"
						label="Search for prescriptions"
						placeholder="Search for prescriptions"
						value={searchQuery}
						onChange={handleSearchChange}
						hideLabel
						isInputGroupIcon
						autoComplete="off"
						data-testid="prescription-list-search"
					>
						{searchQuery.length > 0 && (
							<button
								type="button"
								className="absolute right-[4rem] top-1/2 -translate-y-1/2 font-bold"
								onClick={handleSearchClear}
								data-testid="prescription-list-search-clear"
							>
								<span className="sr-only">Clear search</span>
								<Icon icon="mdi:close" />
							</button>
						)}
						<Button
							type="button"
							className="nsw-button--flex"
							style="white"
							onClick={() => {
								/* presentational only — search is reactive */
							}}
							aria-label="Search"
						>
							<Icon icon="mdi:search" />
						</Button>
					</FormGroupText>
				</div>

				<div className="md:w-56">
					<FormGroupSelect
						htmlId="prescription-list-type-filter"
						label="Filter by type"
						placeholder="All prescription types"
						options={TYPE_FILTER_OPTIONS}
						value={typeFilter}
						onChange={handleTypeFilterChange}
						hideLabel
						autoComplete="off"
						data-testid="prescription-list-type-filter"
					/>
				</div>

				<Button
					type="button"
					style="dark"
					onClick={handleDownload}
					disabled={isDownloading || !listcodename}
					className="flex items-center justify-center gap-2 shrink-0 !h-12"
					data-testid="prescription-list-download"
				>
					<span>{isDownloading ? 'Preparing…' : 'Download'}</span>
					<Icon icon="bxs:download" width={24} height={24} />
				</Button>
			</div>

			{isFetching && (
				<div className="bg-white/90 w-full flex justify-center pt-6">
					<Loading />
				</div>
			)}

			{!isFetching && !showEmptyState && (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse text-left"
						data-testid="prescription-list-table"
					>
						<thead>
							<tr>
								{TABLE_COLUMNS.map((label) => (
									<th
										key={label}
										scope="col"
										className="font-bold p-3 align-top border-b border-nsw-grey-04"
									>
										{label}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{filteredRows.map((row) => {
								const isWork = row.kind === 'work'
								const prescriptions = isWork
									? row.work.prescriptions
									: row.study.prescriptions
								return (
									<tr
										key={`${row.kind}-${row.id}`}
										className="border-b border-nsw-grey-04"
									>
										<td className="p-3 align-top">
											{row.author}
										</td>
										<td className="p-3 align-top">
											{isWork
												? renderTitleCell(row.work)
												: row.study.title}
										</td>
										<td className="p-3 align-top">
											{isWork ? row.work.publication : ''}
										</td>
										<td className="p-3 align-top whitespace-pre-line">
											{buildCourseCell(prescriptions)}
										</td>
										<td className="p-3 align-top">
											{isWork
												? row.work['prescription-type']
												: row.study[
														'prescription-type'
												  ]}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			)}

			{showEmptyState && (
				<h4 className="text-center mt-20">
					{"We didn't find any results. "}
					<button
						type="reset"
						className="underline bold nsw-text--brand-dark"
						onClick={handleReset}
					>
						Clear all filters
					</button>
				</h4>
			)}
		</div>
	)
}

export default PrescriptionFullList
